import { useEffect, useRef } from 'react'

import GuideFeature from '@/components/sections/guide/GuideFeature'
import Anchor from '@/components/ui/Anchor'
import Container from '@/components/ui/Container'
import { useData } from '@/hooks/useLocale'
import { useScrollSpy } from '@/hooks/useScrollSpy'

/**
 * The manual (design: Features Guide): a chapter per panel, each feature with
 * numbered steps, beside a contents list (sticky from 900px up) that opens the
 * chapter being read and highlights the feature. `query` (typed in the hero)
 * narrows it to the features that mention it. A card at the end points to help.
 */
export default function ManualSection({ query, onClear }) {
  const { GUIDE } = useData()
  const list = useRef(null)
  const nav = useRef(null)
  const active = useScrollSpy(list, '[data-spy]', query)

  // Keep the highlighted entry in view when the contents list scrolls (900px up).
  useEffect(() => {
    const link = nav.current.querySelector('[aria-current]')
    if (!link) return
    const box = nav.current.getBoundingClientRect()
    const at = link.getBoundingClientRect()
    if (at.bottom > box.bottom) nav.current.scrollTop += at.bottom - box.bottom + 16
    else if (at.top < box.top) nav.current.scrollTop -= box.top - at.top + 16
  }, [active])

  // Number everything first, so a search keeps each feature's place (2.4).
  const q = query.trim().toLowerCase()
  const matches = (feature) =>
    [feature.title, feature.text, ...feature.steps, feature.tip ?? '']
      .join(' ')
      .toLowerCase()
      .includes(q)
  const chapters = GUIDE.chapters
    .map((chapter, ci) => ({
      ...chapter,
      n: ci + 1,
      features: chapter.features
        .map((feature, fi) => ({ ...feature, n: `${ci + 1}.${fi + 1}` }))
        .filter((feature) => !q || matches(feature)),
    }))
    .filter((chapter) => chapter.features.length)
  const total = chapters.reduce((n, chapter) => n + chapter.features.length, 0)

  return (
    <section className="flex-1 bg-white pt-14 pb-30">
      <Container className="grid items-start gap-x-14 gap-y-8 md:grid-cols-[240px_minmax(0,1fr)]">
        <nav
          ref={nav}
          aria-label={GUIDE.contentsLabel}
          className="flex [scrollbar-width:none] flex-col rounded-[20px] bg-[#f8f9fb] p-4 md:sticky md:top-25 md:max-h-[calc(100vh-120px)] md:overflow-y-auto md:rounded-none md:bg-transparent md:p-0"
        >
          <span className="px-3 pb-2.5 text-[11px] font-semibold tracking-[.16em] text-subtle uppercase">
            {GUIDE.contentsLabel}
          </span>
          {chapters.map((chapter) => {
            const on =
              chapter.id === active ||
              chapter.features.some((feature) => feature.id === active)
            return (
              <div key={chapter.id} className="flex flex-col gap-px pb-2.5">
                <a
                  href={`#${chapter.id}`}
                  className={`flex items-center gap-2.5 rounded-[10px] px-3 py-2 text-[15px] font-semibold text-ink hover:bg-surface ${on ? 'bg-surface' : ''}`}
                >
                  <span
                    className={`w-[22px] text-[13px] ${on ? 'text-brand' : 'text-subtle'}`}
                  >
                    {chapter.n}
                  </span>
                  {chapter.title}
                </a>
                {(on || q) && (
                  <div className="ms-6 mt-0.5 hidden flex-col gap-px border-s border-line ps-3 md:flex">
                    {chapter.features.map((feature) => {
                      const here = feature.id === active
                      return (
                        <a
                          key={feature.id}
                          href={`#${feature.id}`}
                          aria-current={here ? 'location' : undefined}
                          className={`flex items-baseline gap-2.5 rounded-lg px-2.5 py-1.5 text-sm hover:text-brand ${here ? 'font-semibold text-brand' : 'font-medium text-muted'}`}
                        >
                          <span
                            className={`min-w-[26px] text-xs ${here ? 'text-brand' : 'text-[#a3a9b5]'}`}
                          >
                            {feature.n}
                          </span>
                          {feature.title}
                        </a>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        <div ref={list} className="flex max-w-185 min-w-0 flex-col">
          {q && (
            <div className="mb-8 flex items-center justify-between gap-3 rounded-2xl bg-surface px-[18px] py-3.5">
              <span className="text-[15px] text-body">
                {GUIDE.results(total, query.trim())}
              </span>
              <button
                type="button"
                onClick={onClear}
                className="h-[34px] shrink-0 cursor-pointer rounded-full bg-white px-3.5 text-[13px] font-semibold text-ink hover:text-brand"
              >
                {GUIDE.clear}
              </button>
            </div>
          )}

          {chapters.map((chapter) => (
            <div key={chapter.id} className="flex flex-col pb-6">
              <div
                id={chapter.id}
                data-spy
                className="mb-3 flex flex-col gap-3 border-b border-[#e3e7ee] pb-7"
              >
                <span className="text-[13px] font-semibold tracking-[.14em] text-brand uppercase">
                  {GUIDE.chapterLabel} {chapter.n}
                </span>
                <h2 className="text-[clamp(30px,3.2vw,40px)] leading-[1.1] font-medium tracking-[-0.025em] text-ink">
                  {chapter.title}
                </h2>
                <p className="text-[17px]/[1.7] text-muted">{chapter.intro}</p>
                <p className="text-sm text-body">
                  <span className="font-semibold">{GUIDE.whoLabel}</span>{' '}
                  {chapter.who}
                </p>
              </div>
              {chapter.features.map((feature) => (
                <GuideFeature key={feature.id} feature={feature} />
              ))}
            </div>
          ))}

          {q && total === 0 && (
            <p className="py-6 text-[15px] text-subtle">{GUIDE.none}</p>
          )}

          <div className="mt-10 flex flex-wrap items-center justify-between gap-5 rounded-3xl bg-ink p-7 text-white">
            <div className="flex flex-col gap-1.5">
              <h2 className="text-xl font-semibold">{GUIDE.help.title}</h2>
              <p className="text-[15px] text-[#b8c6dd]">{GUIDE.help.text}</p>
            </div>
            <Anchor
              href={GUIDE.help.cta.href}
              className="flex h-[46px] items-center rounded-full bg-white px-[22px] text-sm font-semibold text-ink hover:bg-brand-tint"
            >
              {GUIDE.help.cta.label}
            </Anchor>
          </div>
        </div>
      </Container>
    </section>
  )
}
